import { useState, type ReactNode } from "react";
import { NavLink, useLocation } from "react-router";
import { Drawer } from "@salesapp/ui";
import { useCompactViewport } from "../lib/useNarrowViewport";

export type SectionNavItem = {
  to: string;
  label: string;
  end?: boolean;
  dataTour?: string;
};

function MenuIcon() {
  return (
    <svg viewBox="0 0 24 24" className="section-nav-menu-icon" aria-hidden>
      <path
        d="M4 7h16M4 12h16M4 17h16"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function pathMatches(pathname: string, to: string, end?: boolean): boolean {
  if (end) return pathname === to || pathname === `${to}/`;
  return pathname === to || pathname.startsWith(`${to}/`);
}

/**
 * App section tabs (CRM / Timesheet / Expense). Desktop keeps the horizontal
 * strip; phone and tablet collapse into a current-section chip + hamburger drawer.
 */
export function SectionNav({
  label,
  items,
  trailing,
}: {
  label: string;
  items: SectionNavItem[];
  trailing?: ReactNode;
}) {
  const location = useLocation();
  const narrow = useCompactViewport();
  const [open, setOpen] = useState(false);
  const active =
    items.find((item) => pathMatches(location.pathname, item.to, item.end)) ?? items[0] ?? null;

  if (!narrow) {
    return (
      <nav className="crm-section-nav crm-section-nav--desktop" aria-label={label}>
        {items.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.end}
            className={({ isActive }) => `crm-section-link${isActive ? " is-active" : ""}`}
            data-tour={item.dataTour}
          >
            {item.label}
          </NavLink>
        ))}
        {trailing}
      </nav>
    );
  }

  return (
    <div className="crm-section-nav-bar crm-section-nav--mobile">
      <div className="crm-section-nav-current">
        <span className="crm-section-nav-current-label">{label}</span>
        <strong>{active?.label ?? "Menu"}</strong>
      </div>
      <button
        type="button"
        className="crm-section-nav-menu-btn"
        aria-label={`Open ${label} menu`}
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <MenuIcon />
        <span>Menu</span>
      </button>
      <Drawer isOpen={open} onClose={() => setOpen(false)} title={label} side="left">
        <nav className="crm-section-drawer-nav" aria-label={label}>
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `crm-section-drawer-link${isActive ? " is-active" : ""}`
              }
              data-tour={item.dataTour}
              onClick={() => setOpen(false)}
            >
              {item.label}
            </NavLink>
          ))}
        </nav>
        {trailing ? <div className="crm-section-drawer-trailing">{trailing}</div> : null}
      </Drawer>
    </div>
  );
}
